import axios from 'axios';
import api from './api';

export type BackupDownloadLink = {
  href: string;
  filename: string;
  expiresInSec: number;
};

function apiBasePath(): string {
  if (import.meta.env.VITE_API_URL) {
    return String(import.meta.env.VITE_API_URL).replace(/\/$/, '');
  }
  if (import.meta.env.DEV) {
    return '/api';
  }
  return '/api';
}

/** サーバー発行の直ダウンロード URL（?dl= トークン付き・Vite プロキシ経由の /api） */
export function buildBackupDownloadHref(downloadPath: string): string {
  if (downloadPath.startsWith('http://') || downloadPath.startsWith('https://')) {
    return downloadPath;
  }
  const path = downloadPath.startsWith('/') ? downloadPath : `/${downloadPath}`;
  if (path.startsWith('/api/')) {
    return path;
  }
  const base = apiBasePath();
  return `${base}${path}`;
}

function isRetryableDownloadTokenError(err: unknown): boolean {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status == null) return true;
    return status >= 500 || status === 429;
  }
  const maybe = err as { isNetworkError?: boolean };
  return Boolean(maybe.isNetworkError);
}

async function postBackupDownloadToken<T>(
  url: string,
  body?: Record<string, string>
): Promise<T> {
  const maxAttempts = 3;
  let lastError: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const response = await api.post<{ success: boolean; data: T }>(url, body ?? {});
      const data = response.data?.data;
      if (data == null) {
        throw new Error('ダウンロードリンクを取得できませんでした。');
      }
      return data;
    } catch (err) {
      lastError = err;
      if (!isRetryableDownloadTokenError(err) || attempt === maxAttempts - 1) {
        throw err;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  throw lastError;
}

export async function createJobBackupDownloadLink(jobId: string): Promise<BackupDownloadLink> {
  const data = await postBackupDownloadToken<{
    downloadPath: string;
    filename: string;
    expiresInSec: number;
  }>(`/admin/database/backup/jobs/${encodeURIComponent(jobId)}/download-token`);
  if (!data?.downloadPath) {
    throw new Error('ダウンロードリンクを取得できませんでした。');
  }
  return {
    href: buildBackupDownloadHref(data.downloadPath),
    filename: data.filename,
    expiresInSec: data.expiresInSec,
  };
}

export async function createSavedBackupDownloadLink(relativePath: string): Promise<BackupDownloadLink> {
  const data = await postBackupDownloadToken<{
    downloadPath: string;
    filename: string;
    expiresInSec: number;
  }>('/admin/database/backup/saved-download-token', { path: relativePath });
  if (!data?.downloadPath) {
    throw new Error('ダウンロードリンクを取得できませんでした。');
  }
  return {
    href: buildBackupDownloadHref(data.downloadPath),
    filename: data.filename,
    expiresInSec: data.expiresInSec,
  };
}

/** 同一オリジンの attachment GET（Chrome ダウンロード一覧向け） */
export function triggerNativeBackupDownload(href: string, filename: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename.replace(/[/\\?%*:|"<>]/g, '_') || 'download.sql';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export type DatabaseBackupJobStatus = {
  jobId: string;
  status: 'running' | 'ready' | 'failed';
  elapsedSec: number;
  filename: string | null;
  size: number | null;
  error: string | null;
};

export type DatabaseBackupDownloadProgress = {
  loaded: number;
  total?: number;
};

export interface SavedBackupItem {
  kind: 'daily' | 'weekly' | 'monthly' | 'yearly';
  path: string;
  filename: string;
  dateLabel: string;
  size: number;
  createdAt: string;
}

export interface SavedBackupsList {
  daily: SavedBackupItem[];
  weekly: SavedBackupItem[];
  monthly: SavedBackupItem[];
  yearly: SavedBackupItem[];
}

async function parseBlobApiError(data: Blob): Promise<string> {
  try {
    const text = await data.text();
    const parsed = JSON.parse(text) as {
      message?: string;
      error?: { message?: string };
    };
    return parsed.error?.message || parsed.message || text;
  } catch {
    return 'バックアップの取得に失敗しました。';
  }
}

function filenameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null;
  const m = /filename\*?=(?:UTF-8''|")?([^";\n]+)/i.exec(header);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1].replace(/"/g, '').trim());
  } catch {
    return m[1].replace(/"/g, '').trim();
  }
}

export async function startDatabaseBackupJob(): Promise<string> {
  const response = await api.post<{ success: boolean; data: { jobId: string } }>(
    '/admin/database/backup/jobs'
  );
  const jobId = response.data?.data?.jobId;
  if (!jobId) throw new Error('バックアップジョブを開始できませんでした。');
  return jobId;
}

export async function getDatabaseBackupJobStatus(jobId: string): Promise<DatabaseBackupJobStatus> {
  const response = await api.get<{ success: boolean; data: DatabaseBackupJobStatus }>(
    `/admin/database/backup/jobs/${encodeURIComponent(jobId)}`,
    { timeout: 120000 }
  );
  const data = response.data?.data;
  if (!data) throw new Error('ジョブ状態を取得できませんでした。');
  return data;
}

/** 完了したジョブの SQL を Bearer 認証で Blob 取得 */
export async function downloadJobBackupFile(
  jobId: string,
  options?: {
    onProgress?: (progress: DatabaseBackupDownloadProgress) => void;
  }
): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await api.get(
      `/admin/database/backup/jobs/${encodeURIComponent(jobId)}/file`,
      {
        responseType: 'blob',
        timeout: 600000,
        onDownloadProgress: (event) => {
          options?.onProgress?.({
            loaded: event.loaded,
            total: event.total && event.total > 0 ? event.total : undefined,
          });
        },
      }
    );
    const blob = response.data as Blob;
    if (blob.type?.includes('json')) {
      throw new Error(await parseBlobApiError(blob));
    }
    const fromHeader = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined
    );
    return { blob, filename: fromHeader || `kensuke_backup_${Date.now()}.sql` };
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
      const message = await parseBlobApiError(err.response.data);
      const wrapped = new Error(message) as Error & { httpStatus?: number };
      wrapped.httpStatus = err.response.status;
      throw wrapped;
    }
    const ax = err as { response?: { data?: Blob }; message?: string };
    if (ax.response?.data instanceof Blob) {
      throw new Error(await parseBlobApiError(ax.response.data));
    }
    throw err;
  }
}

export async function listSavedBackups(): Promise<SavedBackupsList> {
  const response = await api.get<{ success: boolean; data: SavedBackupsList }>(
    '/admin/database/backup/list'
  );
  return response.data.data;
}

export async function downloadSavedBackupFile(
  relativePath: string
): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await api.get('/admin/database/backup/saved-file', {
      params: { path: relativePath },
      responseType: 'blob',
      timeout: 600000,
    });
    const blob = response.data as Blob;
    if (blob.type?.includes('json')) {
      throw new Error(await parseBlobApiError(blob));
    }
    const fromHeader = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined
    );
    const fallbackName = relativePath.split('/').pop() || `kensuke_backup_${Date.now()}.sql`;
    return { blob, filename: fromHeader || fallbackName };
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
      throw new Error(await parseBlobApiError(err.response.data));
    }
    const ax = err as { response?: { data?: Blob }; message?: string };
    if (ax.response?.data instanceof Blob) {
      throw new Error(await parseBlobApiError(ax.response.data));
    }
    throw err;
  }
}

export async function restoreDatabase(
  file: File,
  confirm: string
): Promise<{ success: boolean; message: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('confirm', confirm);
  const response = await api.post<{ success: boolean; message: string }>(
    '/admin/database/restore',
    form,
    { timeout: 300000 }
  );
  return response.data;
}

export async function restoreSavedBackup(
  path: string,
  confirm: string
): Promise<{ success: boolean; message: string }> {
  const response = await api.post<{ success: boolean; message: string }>(
    '/admin/database/restore-saved',
    { path, confirm },
    { timeout: 300000 }
  );
  return response.data;
}
